import { useEffect, useMemo } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetPerformanceTestsQuery } from "store/api/performanceTestApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDevicesForPerformanceTest } from "store/tempDataSlise"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { convertDataToPerformanceTestTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"

import { dummyDevices, dummyMeta } from "../MonitoringPage/dummyMonitoringData"
import PerformanceTestContent from "./PerformanceTestContent"
import performanceTestTableHeaderCells from "./performanceTestTableHeaderCells"

const PerformanceTestPage = () => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [triggerGetPerformanceTest, { isFetching, data }] = useLazyGetPerformanceTestsQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const requestParams = { phisical_status: "installed", ...urlQueryParams }
  const hasPerformanceTestAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.PerformanceTest)
    : false
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const isFetchingData = isFetching && !data
  const meta = hasPerformanceTestAccess ? data?.meta : dummyMeta
  const devices = useMemo(() => {
    if (!hasPerformanceTestAccess) {
      return dummyDevices
    }

    return data?.data || []
  }, [data, hasPerformanceTestAccess])

  useEffect(() => {
    triggerGetTableColumns({ list_name: "contractor_performance_test_list" })
  }, [])

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(performanceTestTableHeaderCells, columnsData?.data?.config)
        : performanceTestTableHeaderCells,
    [columnsData],
  )

  const { handleExport } = useExportCSV({
    params: requestParams,
    headerCells: tableHeaderCells,
    meta: meta,
    fileName: "performance_tests",
    convertData: convertDataToPerformanceTestTableRows,
    useLazyQuery: useLazyGetPerformanceTestsQuery,
  })

  const sendRequest = async (params: UITypes.Params) => {
    if (!hasPerformanceTestAccess) {
      return
    }

    try {
      const res = await triggerGetPerformanceTest(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    dispatch(setSelectedDevicesForPerformanceTest({ devices: [] }))
  }, [])

  const fetchPerformanceTestData = async () => {
    sendRequest(requestParams)
  }

  useEffect(() => {
    fetchPerformanceTestData()
  }, [urlQueryParams])

  const handleActionSuccess = () => {
    fetchPerformanceTestData()
  }

  return (
    <>
      <PerformanceTestContent
        devices={devices}
        meta={meta}
        isFetching={isFetchingData}
        onActionSuccess={handleActionSuccess}
        tableHeaderCells={tableHeaderCells}
        columnsLoaded={!!columnsData}
        onExport={handleExport}
      />
      {!hasPerformanceTestAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.PerformanceTest} />}
    </>
  )
}

export default PerformanceTestPage
